import { useEffect, useState, type CSSProperties } from "react";
import { getCashDayStaff, type OnShiftPerson } from "../../lib/api";

/**
 * Who clocked in on Square for this cash business day, so a manager knows who
 * to call about a count. Managers/owners only: the page renders it only for
 * them and bar-cash-day-staff enforces it. Plan: docs/plans/2026-10-02-who-worked-each-day.md.
 */
export default function OnShiftPanel({ businessDate }: { businessDate: string }) {
  const [staff, setStaff] = useState<OnShiftPerson[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    setStaff(null);
    setFailed(false);
    getCashDayStaff(businessDate)
      .then((r) => live && setStaff(Array.isArray(r.staff) ? r.staff : []))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [businessDate]);

  // The bar's clock, whatever the viewing device is set to.
  const time = (iso: string) =>
    new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/Chicago" });

  return (
    <div style={s.wrap}>
      <div style={s.heading}>On Shift (Square clock-ins)</div>
      {failed && <div style={s.muted}>Could not load who was on shift.</div>}
      {!failed && staff === null && <div style={s.muted}>Loading…</div>}
      {staff && staff.length === 0 && <div style={s.muted}>No Square clock-ins for this day.</div>}
      {staff && staff.length > 0 && (
        <div style={s.list}>
          {staff.map((p, i) => (
            <div key={p.name + i} style={s.row}>
              <div style={{ minWidth: 0 }}>
                <div style={s.name}>{p.name}</div>
                <div style={s.sub}>
                  {p.job_titles.join(", ") || "—"} ·{" "}
                  {p.shifts.map((sh) => `${time(sh.start_at)}–${sh.end_at ? time(sh.end_at) : "still clocked in"}`).join(", ")}
                </div>
              </div>
              {p.phone ? (
                <div style={s.actions}>
                  <a href={`tel:${p.phone}`} style={s.btn} aria-label={`Call ${p.name}`}>
                    Call
                  </a>
                  <a href={`sms:${p.phone}`} style={s.btn} aria-label={`Text ${p.name}`}>
                    Text
                  </a>
                </div>
              ) : (
                <span style={s.muted}>No phone in Square</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const s: Record<string, CSSProperties> = {
  wrap: { marginTop: 20 },
  heading: {
    fontSize: 14,
    fontWeight: 800,
    color: "var(--gold, #F9AF36)",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: 10,
  },
  list: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 8,
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    padding: "10px 14px",
    borderTop: "1px solid rgba(255,255,255,0.04)",
    flexWrap: "wrap",
  },
  name: { fontSize: 14, fontWeight: 700, color: "var(--text, #F0E6D4)" },
  sub: { fontSize: 12, color: "var(--muted, #8A7A66)", overflowWrap: "anywhere" },
  actions: { display: "flex", gap: 8 },
  btn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 44,
    minWidth: 64,
    padding: "0 14px",
    borderRadius: 8,
    border: "1px solid var(--gold, #F9AF36)",
    color: "var(--gold, #F9AF36)",
    fontWeight: 700,
    fontSize: 13,
    textDecoration: "none",
  },
  muted: { fontSize: 13, color: "var(--muted, #8A7A66)" },
};
